import i18next from 'i18next';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getSocket } from '../../services/socket';
import { RootState } from '../../store';
import { profileRequest } from '../../store/auth/authActions';
import {
	fillTranslationPlaceholders,
	formatChipsForBroadcast,
} from '../../utils/helpers/globalBroadcastMessage';
import { showGameBroadcastToast } from './showGameBroadcastToast';

/** Pushed by trinity-nest after it auto-grants the one-time Anti-Churn Bonus. */
const ANTI_CHURN_BONUS_GRANTED_EVENT = 'antiChurnBonusGranted';

type AntiChurnBonusPacket = {
	rewardType?: string;
	amount?: number;
	newBalance?: number;
};

/**
 * Tells the player about the Anti-Churn Bonus the server granted when their chips first ran out,
 * and refreshes the profile so the new balance shows. Uses its own event name: the PixiJS game
 * clears listeners on the shared socket for the events it owns (getUserProfile, buyIn, ...).
 */
const AntiChurnBonusListener = () => {
	const auth = useSelector((s: RootState) => s.auth);
	const dispatch = useDispatch();
	const isLoggedIn = !!auth?.user;
	const playerId = auth?.user?.id || '';
	const username = auth?.user?.username || '';

	useEffect(() => {
		if (!isLoggedIn || !playerId || !username) {
			return;
		}

		const socket = getSocket(String(playerId), String(username));

		const onBonusGranted = (packet: AntiChurnBonusPacket) => {
			const title = i18next.t('T_SITE_ANTI_CHURN_TITLE', { defaultValue: 'Back in action!' });
			const template = i18next.t('T_SITE_ANTI_CHURN_BODY', {
				defaultValue: "%% Chips have landed in your balance. Let's play!",
			});
			const body = fillTranslationPlaceholders(template, [formatChipsForBroadcast(packet?.amount)]);

			showGameBroadcastToast('rewardBonus', title, undefined, body);
			dispatch(profileRequest());
		};

		socket.on(ANTI_CHURN_BONUS_GRANTED_EVENT, onBonusGranted);

		return () => {
			socket.off(ANTI_CHURN_BONUS_GRANTED_EVENT, onBonusGranted);
		};
	}, [isLoggedIn, playerId, username, dispatch]);

	return null;
};

export default AntiChurnBonusListener;
